import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table } from 'antd';
import { useBibleTranslations, useChaptersSearch, useGetBibleUrlParams } from 'core/hooks';
import Container from 'components/common/container';
import MemosModal from 'components/common/Modals/memosModal';
import SearchModal from 'components/common/Modals/SearchModal';
import { openNotificationWithIcon } from 'config/notification';
import { IBibleBook } from 'types';
import { NotificationType } from 'store/slices/auth/types';
import { useMemoSelector, useAppDispatch } from 'store/hooks';
import { getAuthState } from 'store/selectors/auth';
import { getBookmarks as fetchBookmarks } from 'store/slices/bookmarks/actions';
import biblesSlice from 'store/slices/biblesSlice';
import { getCertainNotes } from 'store/slices/notes/actions';

import { NOTES_VALUE } from './Header/TranslationsSelector';
import { useReadingPageData } from './useReadingPageData';
import { useVersesTable } from './useVersesTable';
import BooksViewerLoader from './Loaders/BooksViewerLoader';
import VerseActions from './VerseActions';
import SearchButtonsRow from './SearchButtonsRow';
import Search from './Search';
import { VerseRowRecord } from './types';

function BooksViewer() {
  const dispatch = useAppDispatch();
  const { userInfo } = useMemoSelector(getAuthState);
  const { bibleTranslations } = useBibleTranslations();
  const { book, chapter, verse, translations } = useGetBibleUrlParams();

  const translationParts = translations ? translations.split(',') : [];
  const translationIds = translationParts
    .filter(p => p !== NOTES_VALUE)
    .map(Number)
    .filter(n => !Number.isNaN(n));
  const notesShown = translationParts.includes(NOTES_VALUE) && Boolean(userInfo?._id);
  const location = useLocation();
  const navigate = useNavigate();
  const { books, readingData, loading } = useReadingPageData();

  const {
    columns: tableColumns,
    tableData,
    selectedVerses,
    setSelectedVerses,
    notificationContextHolder,
    notificationApi,
  } = useVersesTable();

  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [translationForSearch, setTranslationForSearch] = useState<number | null>(null);
  const [highlightedVerse, setHighlightedVerse] = useState<number | null>(null);

  const { searchResult, searchForWord } = useChaptersSearch();

  useEffect(() => {
    if (userInfo?._id) {
      dispatch(fetchBookmarks());
      dispatch(getCertainNotes({ bookNumber: book, chapterNumber: chapter }));
    }
  }, [dispatch, userInfo?._id, book, chapter]);

  const paramsRef = useRef<{
    book: number;
    chapter: number;
    verse: number;
    translations: number[];
    books: IBibleBook[];
  } | null>(null);

  useEffect(() => {
    if (book && chapter && translations && books.length > 0) {
      paramsRef.current = {
        book,
        chapter,
        verse,
        translations: translationIds,
        books,
      };
    }
  }, [book, chapter, verse, translations, books, translationIds]);

  useEffect(() => () => {
    if (paramsRef.current && paramsRef.current.books.length > 0) {
      const { book: bookNumber, chapter: chapterNumber, verse: verseNumber, translations: ids, books: booksData } = paramsRef.current;
      const firstBook = booksData[0];

      dispatch(biblesSlice.actions.setBibleUrlParams({
        book: {
          number: bookNumber,
          chaptersCount: firstBook.chapters_count,
        },
        chapter: chapterNumber,
        verse: verseNumber,
        translations: ids,
      }));
    }
  }, [dispatch]);

  useEffect(() => {
    const highlight = (location.state as { highlight?: boolean })?.highlight;
    if (!readingData || !verse || !highlight) return;

    setHighlightedVerse(verse);

    const scrollToVerse = () => {
      const rowEl = document.getElementById(`verse-row-${verse}`);
      rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const scrollTimeoutId = window.setTimeout(scrollToVerse, 100);

    window.setTimeout(() => {
      setHighlightedVerse(null);
      navigate(location.pathname, { replace: true, state: {} });
    }, 3000);

    return () => {
      window.clearTimeout(scrollTimeoutId);
    };
  }, [readingData, verse, location.state, location.pathname, navigate]);

  if (loading) {
    return <BooksViewerLoader />;
  }

  if (!readingData) {
    return (
      <Container fluid style={{ padding: 0 }}>
        <div style={{ padding: '16px' }} />
      </Container>
    );
  }

  const handleMemosModalClose = (successMessage?: string | null) => {
    setIsMemoModalOpen(false);
    setSelectedVerses([]);
    if (successMessage) {
      openNotificationWithIcon({
        api: notificationApi,
        type: NotificationType.success,
        description: successMessage,
        placement: 'top',
      });
    }
  };

  const handleSearchClick = (translationId: number) => {
    setTranslationForSearch(translationId);
    setIsSearchModalOpen(true);
  };

  const handleSearchModalClose = () => {
    setIsSearchModalOpen(false);
    setTranslationForSearch(null);
  };

  const handleSearchModalMinimize = () => {
    setIsSearchModalOpen(false);
    setTranslationForSearch(null);
  };

  const translationCount = readingData.chapters.length + (notesShown ? 1 : 0);
  const contentModifier = translationCount === 1
    ? 'bible-v2-viewer-content__one-translation'
    : translationCount === 2
      ? 'bible-v2-viewer-content__two-translations'
      : 'bible-v2-viewer-content__many-translations';

  return (
    <>
      {notificationContextHolder}
      {userInfo?._id && selectedVerses.length > 0 && (
        <VerseActions handleMemoClick={() => setIsMemoModalOpen(true)} />
      )}
      <div className={`bible-v2-viewer-content ${contentModifier}`}>
        <SearchButtonsRow
          chapters={readingData.chapters}
          translationIds={translationIds}
          bibleTranslations={bibleTranslations}
          onSearchClick={handleSearchClick}
          notesShown={notesShown}
        />
        <Table<VerseRowRecord>
          size="middle"
          bordered
          pagination={false}
          columns={tableColumns}
          dataSource={tableData}
          onRow={record => ({ id: `verse-row-${record.verse}` })}
          rowClassName={record => {
            const isHighlighted = highlightedVerse === record.verse;
            return [
              isHighlighted ? 'verse-row-highlight' : '',
            ].filter(Boolean).join(' ') || '';
          }}
        />
      </div>
      {isMemoModalOpen && userInfo?._id && selectedVerses.length > 0 && (
        <MemosModal
          isOpen={isMemoModalOpen}
          selectedVerses={selectedVerses}
          onClose={handleMemosModalClose}
        />
      )}
      {isSearchModalOpen && translationForSearch !== null && (
        <SearchModal
          title={bibleTranslations.find(t => t.id === translationForSearch)?.name || 'Search'}
          open={isSearchModalOpen}
          onCancel={handleSearchModalClose}
          onMinimize={handleSearchModalMinimize}
        >
          <Search
            translationId={translationForSearch}
            searchResult={searchResult}
            searchForWord={searchForWord}
            onClose={handleSearchModalClose}
            currentBookNumber={book ?? undefined}
          />
        </SearchModal>
      )}
    </>
  );
}

export default BooksViewer;
